import { Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import * as io from 'socket.io-client';
import { environment } from '@env/environment';

/**
 * Evento "poke" emitido por el backend cuando cambia una solicitud. No trae la
 * lista: el componente refresca en silencio su página actual al recibirlo.
 */
export interface SolicitudCambioEvent {
    type: 'creada' | 'actualizada' | 'estado';
    solicitudId: string;
    lastState_numberState?: number;
}

/**
 * Socket de la bandeja de autorizaciones (namespace `/solicitudes`).
 *
 * Sigue el mismo patrón que `ChatSocketService` (token en el handshake,
 * `NgZone` para change detection, reconexión). Es autocontenido: no toca el
 * chat y se conecta solo mientras la bandeja está abierta.
 */
@Injectable({ providedIn: 'root' })
export class SolicitudesSocketService {
    private socket: any = null;

    private readonly cambioSubject = new Subject<SolicitudCambioEvent>();
    readonly cambio$: Observable<SolicitudCambioEvent> =
        this.cambioSubject.asObservable();

    constructor(private zone: NgZone) {}

    conectar(token: string): void {
        if (this.socket || !token) {
            return;
        }

        this.socket = io(this.socketUrl() + '/solicitudes', {
            path: this.socketPath(),
            query: { token },
            transports: ['websocket'],
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 2000,
        });

        this.socket.on('solicitud:cambio', (evento: SolicitudCambioEvent) =>
            this.zone.run(() => this.cambioSubject.next(evento)),
        );
    }

    desconectar(): void {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
    }

    /** Host del backend sin el sufijo `/api`. */
    private socketUrl(): string {
        return environment.urlBackend
            .replace(/\/api\/?$/, '')
            .replace(/\/+$/, '');
    }

    /** Path del endpoint de socket.io, respetando el prefijo `/api` de prod. */
    private socketPath(): string {
        return /\/api\/?$/.test(environment.urlBackend)
            ? '/api/socket.io'
            : '/socket.io';
    }
}
